import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';

// Key names that differ by language come from i18n (shortcuts.keys).
const ROWS: Array<[string, string]> = [
  ['⌘/Ctrl + {enter}', 'session'],
  ['H', 'hangup'],
  ['⌘/Ctrl + P', 'hangupQualify'],
  ['M', 'mute'],
  ['D', 'keypad'],
  ['N', 'note'],
  ['1 – 8', 'outcome'],
  ['{enter}', 'save'],
  ['{space}', 'extend'],
  ['U', 'undo'],
  ['{escape}', 'escape'],
  ['?', 'help'],
];

interface Props {
  open: boolean;
  onOpenChange(open: boolean): void;
  singleKeys: boolean;
  onSingleKeys(on: boolean): void;
}

/** Spec §4.6 + DES-3: the shortcuts, and the switch for single-key ones (WCAG 2.1.4). */
export function ShortcutHelp({ open, onOpenChange, singleKeys, onSingleKeys }: Props) {
  const { t } = useTranslation(['dialer']);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('dialer:callPage.shortcuts.title')}</DialogTitle>
          <DialogDescription>{t('dialer:callPage.shortcuts.description')}</DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          {ROWS.map(([keys, id]) => (
            <div key={id} className="contents">
              <dt><kbd className="rounded border px-1.5 py-0.5 font-mono text-xs" style={{ borderColor: 'var(--vf-border)' }}>
                {keys.replace(/\{(enter|space|escape)\}/g, (_m, k: string) => t(`dialer:callPage.shortcuts.keys.${k}`))}
              </kbd></dt>
              <dd style={{ color: 'var(--vf-text-1)' }}>{t(`dialer:callPage.shortcuts.list.${id}`)}</dd>
            </div>
          ))}
        </dl>
        <label className="mt-2 flex items-center justify-between gap-3 text-sm">
          <span>{t('dialer:callPage.shortcuts.singleKeys')}</span>
          <Switch checked={singleKeys} onCheckedChange={onSingleKeys} />
        </label>
      </DialogContent>
    </Dialog>
  );
}

/** The strip under the page: the active mode, and the keys of the phase. */
export function ShortcutStrip({ mode }: { mode: 'input' | 'commands' }) {
  const { t } = useTranslation(['dialer']);
  return (
    <div
      className="flex h-[34px] items-center gap-4 overflow-x-auto border-t px-4 text-[11px]"
      style={{ borderColor: 'var(--vf-border)', color: 'var(--vf-text-2)', background: 'var(--vf-surface-1)' }}
    >
      <span className="font-semibold" style={{ color: 'var(--vf-text-1)' }}>
        {mode === 'input' ? t('dialer:callPage.shortcuts.modeInput') : t('dialer:callPage.shortcuts.modeCommands')}
      </span>
      <span>{t('dialer:callPage.shortcuts.strip')}</span>
    </div>
  );
}
